import { comparator } from '@fi-shared/utils/array-sort-comparators';
import { ApplicationSettingsModel } from './application-settings.model';
import { CustomButtonModel } from '@fi-app/core/custom-button.model';
import { FiApplicationLanguageType } from '@fi-shared/fi-content-language-shared/fi-application-language-type';
import { ContentLanguagesService } from '@fi-app/utilities/fi-services/content-languages.service';
import { Injector } from '@angular/core';
import { AccessRightType, ApplicationSettingsType, ModuleType } from '@fi-shared/types';
import {
    FiCustomerDataService,
    IFiCustomerDataCustomButton,
} from '@fi-shared/fi-shared-services/fi-customer-data.service';
import { isDefined } from '@fi-shared/utils';
import { ILightWeightUser } from '@fi-shared/models';
import { IContentLanguageLabelModel } from '@fi-shared/fi-content-language-shared/fi-content-language-shared';
import { IUserProfile } from '@fi-shared/profile-icon/profile-icon.component';
import { ICreateChangeBase } from '@fi-shared/models/models';
import { ExtendedRoleRightModel, IExtendedRoleRightModel } from '@fi-app/role/extended-role-right.model';

export interface IRoleModel extends ICreateChangeBase {
    Id: number;
    Name: string;
    Description: string;
    Names: IContentLanguageLabelModel[];
    Descriptions: IContentLanguageLabelModel[];
    RoleUsers: ILightWeightUser[];
    UserCount: number;
    IsSystemObject: boolean;
    HasAccessToPosts: boolean;
    HasAccessToForums: boolean;
    HasAccessToDocuments: boolean;
    HasAccessToMediaBankFiles: boolean;
    CustomButtons: IFiCustomerDataCustomButton[];
    ExtendedRoleRight: IExtendedRoleRightModel;
}

export class RoleModel implements ICreateChangeBase {
    Id = 0;
    Name = '';
    Description = '';
    UserCount = 0;
    RoleUsers: ILightWeightUser[] = [];

    RoleApplicationSettings: ApplicationSettingsModel[] = [];
    CustomButtons: CustomButtonModel[] = [];

    Names: IContentLanguageLabelModel[] = [];
    Descriptions: IContentLanguageLabelModel[] = [];

    CreatedByUserId = 0;
    CreatedByUserName = '';
    CreatedOn: string; //ISO 8601 date
    ChangedByUserId = 0;
    ChangedByUserName = '';
    ChangedOn: string; //ISO 8601 date
    IsSystemObject = false;
    HasAccessToPosts: boolean;
    HasAccessToForums: boolean;
    HasAccessToDocuments: boolean;
    HasAccessToMediaBankFiles: boolean;
    CreatedByProfile: IUserProfile;
    ChangedByProfile: IUserProfile;

    ExtendedRoleRight: ExtendedRoleRightModel;

    constructor(applicationLanguageType: FiApplicationLanguageType, role: IRoleModel, injector: Injector) {
        if (role) {
            this.Initialize(applicationLanguageType, role, injector);
        } else {
            this.InitializeRoleApplicationSettings(role, injector);
            this.InitializeRoleCustomButtons(applicationLanguageType, role, injector);
            this.InitializeContentLanguagesArray(role, injector);
        }

        this.ExtendedRoleRight = new ExtendedRoleRightModel(ModuleType.Core, role?.ExtendedRoleRight);
    }

    Initialize(applicationLanguageType: FiApplicationLanguageType, role: IRoleModel, injector: Injector) {
        this.Id = role.Id;
        this.Name = role.Name;
        this.Description = role.Description;
        this.UserCount = role.UserCount;
        this.RoleUsers = role.RoleUsers;

        this.CreatedByUserId = role.CreatedByUserId;
        this.CreatedByUserName = role.CreatedByUserName;
        this.CreatedOn = role.CreatedOn;
        this.ChangedByUserId = role.ChangedByUserId;
        this.ChangedByUserName = role.ChangedByUserName;
        this.ChangedOn = role.ChangedOn;

        this.IsSystemObject = role.IsSystemObject || false;
        this.HasAccessToPosts = role.HasAccessToPosts;
        this.HasAccessToForums = role.HasAccessToForums;
        this.HasAccessToDocuments = role.HasAccessToDocuments;
        this.HasAccessToMediaBankFiles = role.HasAccessToMediaBankFiles;
        this.CreatedByProfile = role.CreatedByProfile;
        this.ChangedByProfile = role.ChangedByProfile;
        this.InitializeRoleApplicationSettings(role, injector);
        this.InitializeRoleCustomButtons(applicationLanguageType, role, injector);

        this.InitializeContentLanguagesArray(role, injector);
    }

    InitializeContentLanguagesArray(role: IRoleModel, injector: Injector) {
        const contentLanguagesService = injector.get(ContentLanguagesService);
        contentLanguagesService.InitializeContentLanguagesArray(this.Names, role ? role.Names : [], true);
        contentLanguagesService.InitializeContentLanguagesArray(this.Descriptions, role ? role.Descriptions : [], true);
    }

    InitializeRoleApplicationSettings(role, injector: Injector) {
        const isSystemRole = !!role && role.IsSystemObject;
        const customerDataService = injector.get(FiCustomerDataService);
        const applicationSettingAccessTypes = customerDataService.customerData.ApplicationSettingAccessTypes;

        applicationSettingAccessTypes
            .filter((applicationSettingAccessType) => !applicationSettingAccessType.IsSystemObject || isSystemRole)
            .forEach((applicationSettingAccessType) => {
                const accessRightTypes = applicationSettingAccessType.AccessRightTypes.sort(comparator((x) => x));
                let settingAdded = false;
                if (role) {
                    for (let y = 0; y < role.RoleApplicationSettings.length; y++) {
                        if (
                            role.RoleApplicationSettings[y].ApplicationSettingType ==
                            applicationSettingAccessType.ApplicationSettingType
                        ) {
                            this.RoleApplicationSettings.push(
                                new ApplicationSettingsModel(
                                    this.Id,
                                    applicationSettingAccessType.ApplicationSettingType,
                                    role.RoleApplicationSettings[y].AccessRightType,
                                    accessRightTypes,
                                ),
                            );

                            settingAdded = true;
                            break;
                        }
                    }
                }

                if (!settingAdded) {
                    let accessRightType = AccessRightType.UnAvailable;
                    if (this.isNewRole(role)) {
                        // default access levels for new role
                        if (
                            [
                                ApplicationSettingsType.CoreLocal,
                                ApplicationSettingsType.DocumentLocal,
                                ApplicationSettingsType.MediaBankLocal,
                                ApplicationSettingsType.PostLocal,
                                ApplicationSettingsType.Forum,
                                ApplicationSettingsType.ChecklistV2Local,
                                ApplicationSettingsType.FormLocal,
                                ApplicationSettingsType.TaskV2Local,
                            ].includes(applicationSettingAccessType.ApplicationSettingType)
                        ) {
                            accessRightType = accessRightTypes[0];
                        }

                        if (
                            [ApplicationSettingsType.Chat].includes(applicationSettingAccessType.ApplicationSettingType)
                        ) {
                            accessRightType = AccessRightType.Admin;
                        }
                    }

                    this.RoleApplicationSettings.push(
                        new ApplicationSettingsModel(
                            this.Id,
                            applicationSettingAccessType.ApplicationSettingType,
                            accessRightType,
                            accessRightTypes,
                        ),
                    );
                }
            });
    }

    private isNewRole(role: IRoleModel): boolean {
        return !isDefined(role) || !role.hasOwnProperty('Id') || !role.Id;
    }

    InitializeRoleCustomButtons(
        applicationLanguageType: FiApplicationLanguageType,
        role: IRoleModel,
        injector: Injector,
    ) {
        const self = this;
        const customerDataService = injector.get(FiCustomerDataService);
        customerDataService.customerData.CustomButtons.forEach((btn) => {
            const button = new CustomButtonModel(
                btn,
                applicationLanguageType,
                customerDataService.customerData.DefaultApplicationLanguageType,
            );
            button.IsSelected =
                role &&
                role.CustomButtons.map(function (btn) {
                    return btn.Id;
                }).indexOf(btn.Id) > -1;
            self.CustomButtons.push(button);
        });
    }
}
